import { Helper } from "../helpers/helper";
import { ImisResponseItems, ImisResponseItemsRaw, ImisResponseResult } from "../models/imis.models";
import { RestUtility } from "./rest.utility";

/**
 * Get all group members for a company or check if a specific user is a member
 * @param companyId 
 * @param userId optional 
 * @returns 
 */
async function getGroupMemberByCompanyUserId(companyId: string, userId?: string): Promise<ImisResponseItemsRaw> {
    let groupMemberResponse = await RestUtility.restApiGetData<ImisResponseItemsRaw>("/api/groupmember", { GroupId: `ORGRELATIONSHIP-${companyId}`, PartyId: userId }, true);
    if (groupMemberResponse == null) {
        throw new Error(`Existing group member record could not be retrieved for companyId: ${companyId} and userId: ${userId}`);
    }

    return groupMemberResponse;
}

/**
 * Determines whether a user is currently an active company administrator for the given company's organization group.
 * @param companyId The ID of the company whose organization group should be checked.
 * @param userId The ID of the user/party to check for active company admin membership.
 * @returns A promise resolving to `true` if the user has an active company admin group member detail, otherwise `false`.
 */
async function isUserCompanyAdminByCompanyUserId(companyId: string, userId: string): Promise<boolean> {
    let isCompanyAdmin = false;

    let groupMemberResponse = await getGroupMemberByCompanyUserId(companyId, userId);
    if (groupMemberResponse.Items.$values.length > 0) {
        // ensure user is already a active company admin, and update if not
        let groupMember = groupMemberResponse.Items.$values[0];
        let companyAdminMembershipDetail = getCompanyAdminGroupMemberDetailsFromGroupMember(groupMember);
        if (companyAdminMembershipDetail != null && companyAdminMembershipDetail.IsActive) {
            isCompanyAdmin = true;
        }
    }

    return isCompanyAdmin;
}

/**
 * Grants a user the company administrator role for a company's organization group, creating a new group member record or reactivating/adding the company admin membership detail on an existing one, then saves the result.
 * @param companyId The ID of the company whose organization group the user should be made an admin of.
 * @param userId The ID of the user/party to set as company admin.
 */
async function setUserAsCompanyAdminByCompanyUserId(companyId: string, userId: string): Promise<void> {
    let payload = getCreateGroupMemberPayload(companyId, userId);
    addRoleToGroupMemberPayload(payload, "COMPANYADMINISTRATOR");

    let groupMemberResponse = await getGroupMemberByCompanyUserId(companyId, userId);
    if (groupMemberResponse.Items.$values.length > 0) {
        let existingPayload = groupMemberResponse.Items.$values[0];
        let companyAdminMembershipDetail = getCompanyAdminGroupMemberDetailsFromGroupMember(existingPayload);
        if (companyAdminMembershipDetail != null) {
            // the group member detail is probably not active, update it
            companyAdminMembershipDetail.IsActive = true;
        } else {
            // the group member detail is not found, create it
            existingPayload.MembershipDetails.$values.push(payload.MembershipDetails.$values[0]);
        }

        payload = existingPayload;
    }

    let createUpdateResponse = await RestUtility.restApiSendData("/api/groupmember", payload);
    if (createUpdateResponse == null) {
        throw new Error(`Group member record could not be created/updated for companyId: ${companyId} and userId: ${userId}`);
    }
}

/**
 * Looks up a dynamic group by name and triggers its refresh, either for the whole group or for a specific party, depending on whether a user ID is supplied.
 * @param groupName The name of the dynamic group to look up and refresh.
 * @param forUserId The ID of the party to refresh membership for. When null/empty, the entire dynamic group is refreshed instead of a single party.
 * @returns A promise resolving to the group refresh execute response.
 */
async function refreshDynamicGroupByName(groupName: string, forUserId: string): Promise<any> {
    let groupDataResponse = await RestUtility.restApiGetData<ImisResponseItems<any>>("/api/group", { name: groupName });
    if (groupDataResponse == null || groupDataResponse.Items.length == 0) {
        throw new Error(`Existing group record could not be retrieved for groupName: ${groupName}`);
    }

    let groupId = groupDataResponse.Items[0].GroupId;
    let isUserIdValid = !Helper.isStringNullOrEmpty(forUserId);
    let payload = {
        "$type": "Asi.Soa.Core.DataContracts.GenericExecuteRequest, Asi.Contracts",
        "OperationName": isUserIdValid ? "RefreshDynamicGroupForParty" : "RefreshDynamicGroup",
        "Parameters": {
            "$values": [
                {
                    "$type": "System.String",
                    "$value": groupId
                }
            ]
        }
    };
    if (isUserIdValid) {
        payload.Parameters.$values.push({
            "$type": "System.String",
            "$value": forUserId
        });
    }

    let refreshGroupResponse = await RestUtility.restApiSendData<ImisResponseResult<any>>("/api/group/_execute", payload);
    if (refreshGroupResponse == null || !refreshGroupResponse.IsSuccessStatusCode) {
        throw new Error(`Group could not be refreshed for group name: ${groupName}`);
    }
    return refreshGroupResponse;
}

/**
 * Appends a new active membership detail with the given role to a group member payload's `MembershipDetails` collection, mutating it in place.
 * @param payload The group member payload whose `MembershipDetails.$values` collection should be appended to.
 * @param roleId The role ID (e.g. "COMPANYADMINISTRATOR") to assign in the new membership detail.
 */
function addRoleToGroupMemberPayload(payload: any, roleId: string) {
    payload.MembershipDetails.$values.push({
        "$type": "Asi.Soa.Membership.DataContracts.OrganizationGroupMemberDetailData, Asi.Contracts",
        "IsActive": true,
        "Role": {
            "$type": "Asi.Soa.Membership.DataContracts.GroupRoleData, Asi.Contracts",
            "RoleId": roleId
        }
    });
}

/**
 * Builds a new, empty active group member payload linking a user to a company's organization group, ready to have membership details added to it.
 * @param companyId The ID of the company whose organization group ("ORGRELATIONSHIP-{companyId}") the payload should reference.
 * @param userId The ID of the party the group member payload is for.
 * @returns A new group member data payload with an empty `MembershipDetails` collection.
 */
function getCreateGroupMemberPayload(companyId: string, userId: string) {
    return {
        "$type": "Asi.Soa.Membership.DataContracts.GroupMemberData, Asi.Contracts",
        "MembershipDetails": {
            "$type": "Asi.Soa.Membership.DataContracts.GroupMemberDetailDataCollection, Asi.Contracts",
            "$values": []
        },
        "Group": {
            "$type": "Asi.Soa.Membership.DataContracts.GroupSummaryData, Asi.Contracts",
            "GroupId": "ORGRELATIONSHIP-" + companyId,
        },
        "Party": {
            "$type": "Asi.Soa.Membership.DataContracts.PartySummaryData, Asi.Contracts",
            "PartyId": userId,
        },
        "IsActive": true
    };
}

/**
 * Extracts the company administrator membership detail (if any) from a group member record's membership details collection.
 * @param groupMemberRecord The group member record to search.
 * @returns The membership detail whose role is "COMPANYADMINISTRATOR", or `undefined` if none is found.
 */
function getCompanyAdminGroupMemberDetailsFromGroupMember(groupMemberRecord: any) {
    let companyAdminMembershipDetail = groupMemberRecord.MembershipDetails.$values.find(detail => detail.Role.RoleId == "COMPANYADMINISTRATOR");
    return companyAdminMembershipDetail;
}

export {
    getGroupMemberByCompanyUserId,
    isUserCompanyAdminByCompanyUserId,
    setUserAsCompanyAdminByCompanyUserId,
    getCreateGroupMemberPayload,
    addRoleToGroupMemberPayload,
    getCompanyAdminGroupMemberDetailsFromGroupMember,
    refreshDynamicGroupByName
};
